import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Box,
  Button,
  Paper,
  Collapse,
  Typography,
  Fade,
  Badge,
  Tooltip,
  Skeleton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import axios from "axios";
import { API_URL, popupTimer } from "../../consts";
import { useDispatch } from "react-redux";
import ConfirmationDialog from "../common/ConfirmationDialog";
import ChipList from "../common/ChipList";
import HomeCardItem from "./HomeCardItem";
import FeedCardItem from "./FeedCardItem";
import AddToSpace from "./AddToSpace";
import { useSelector } from "react-redux";
import ExtensionLoginSuccess from "../Extension/ExtensionLoginSuccess";
import { useNavigate } from "react-router-dom";
import ai from "../../Assets/ai.png";
import AiDialog from "../Ai/AiDialog";
import SpacesFilter from "../common/SpacesFilter";
import MobileFilterView from "../common/MobileFilterView";

const Reviews = ({ organizationId = null, pageTitle = "My Reviews" }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  let navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get search state from Redux
  const searchState = useSelector((state) => state.search);
  const authorized = useSelector((state) => state.authorized);
  const [selectedCards, setSelectedCards] = useState([]);
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [filterBy, setFilterBy] = useState("all");
  // "Exact search" lives in the header search (Redux) so it sits next to the query itself
  const exactSearch = !!searchState.exactSearch;
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removedRateId, setRemovedRateId] = useState(null);

  // Add states for "Add to Space" functionality
  const [openSpaceModal, setOpenSpaceModal] = useState(false);
  const [selectedRateId, setSelectedRateId] = useState(null);

  const [openAiDialog, setOpenAiDialog] = useState(false);
  const getRatesTrigger = useSelector((state) => state.getRatesTrigger);
  const [showSpaceFilter, setShowSpaceFilter] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [mobileSpacesData, setMobileSpacesData] = useState([]);
  const [showMobileSpaceModal, setShowMobileSpaceModal] = useState(false);

  // Add pagination states
  const [ratesPage, setRatesPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreRates, setHasMoreRates] = useState(true);
  const containerRef = useRef(null);

  // Add view mode state - default to 'feed' on mobile, 'grid' on desktop
  const [viewMode, setViewMode] = useState(isMobile ? 'feed' : 'grid');
  const previousViewModeRef = useRef(null); // Store previous view mode when chat opens
  const previousViewModeForResizeRef = useRef(null); // Store previous view mode when resize forces feed

  // Get chat open state from Redux
  const chatOpen = useSelector((state) => state.chatOpen);

  // Update view mode when screen size changes
  useEffect(() => {
    setViewMode(isMobile ? 'feed' : 'grid');
  }, [isMobile]);

  // Handle chat open/close - switch to feed view when chat opens, restore previous when closes
  useEffect(() => {
    if (chatOpen) {
      // Chat opened - save current view mode if not already feed, then switch to feed
      if (viewMode !== 'feed') {
        previousViewModeRef.current = viewMode;
        setViewMode('feed');
      }
    } else {
      // Chat closed - restore previous view mode if it wasn't already feed
      // But only if card width allows it (checked by resize observer)
      if (previousViewModeRef.current && previousViewModeRef.current !== 'feed') {
        // Check if we can restore (card width >= 200px)
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const padding = 32;
          const spacing = 24;
          let cardWidth;
          if (containerWidth >= 960) {
            cardWidth = (containerWidth - padding - spacing * 2) / 3;
          } else if (containerWidth >= 600) {
            cardWidth = (containerWidth - padding - spacing) / 2;
          } else {
            cardWidth = containerWidth - padding;
          }
          
          if (cardWidth >= 200) {
            setViewMode(previousViewModeRef.current);
            previousViewModeRef.current = null;
          }
        }
      }
    }
  }, [chatOpen, viewMode]);

  // Handle resize - switch to feed view when card width < 200px
  useEffect(() => {
    if (!containerRef.current || viewMode === 'feed') return;

    const calculateCardWidth = () => {
      const container = containerRef.current;
      if (!container) return null;

      const containerWidth = container.clientWidth;
      const padding = 32; // Approximate padding (16px on each side)
      const spacing = 24; // Grid spacing

      // Determine breakpoint based on container width
      let cardWidth;
      if (containerWidth >= 960) {
        // md breakpoint: 3 columns
        cardWidth = (containerWidth - padding - spacing * 2) / 3;
      } else if (containerWidth >= 600) {
        // sm breakpoint: 2 columns
        cardWidth = (containerWidth - padding - spacing) / 2;
      } else {
        // xs breakpoint: 1 column
        cardWidth = containerWidth - padding;
      }

      return cardWidth;
    };

    const checkCardWidth = () => {
      const cardWidth = calculateCardWidth();
      if (cardWidth === null) return;

      if (cardWidth < 200 && viewMode !== 'feed') {
        // Card too small - switch to feed view
        if (!previousViewModeForResizeRef.current) {
          previousViewModeForResizeRef.current = viewMode;
        }
        setViewMode('feed');
      } else if (cardWidth >= 200 && previousViewModeForResizeRef.current) {
        // Card width restored - restore previous view mode
        setViewMode(previousViewModeForResizeRef.current);
        previousViewModeForResizeRef.current = null;
      }
    };

    // Check on mount and resize
    checkCardWidth();

    const resizeObserver = new ResizeObserver(() => {
      checkCardWidth();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [viewMode]);

  // Header visibility control for all views
  const headerVisibleTimeoutRef = useRef(null);
  const lastScrollY = useRef(0);
  const isScrolling = useRef(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  
  useEffect(() => {
    // Always show initially
    setHeaderVisible(true);
    
    // Handle scroll for both feed and grid views
    const handleScroll = () => {
      let currentScrollY;
      
      if (viewMode === 'feed') {
        // For feed view, use window scroll
        currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      } else {
        // For grid view, use container scroll
        if (!containerRef.current) return;
        currentScrollY = containerRef.current.scrollTop;
      }
      
      // Always show header at the top
      if (currentScrollY < 10) {
        setHeaderVisible(true);
        isScrolling.current = false;
        
        // Clear existing timeout
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
        lastScrollY.current = currentScrollY;
        return;
      }
      
      // Only hide/show on actual scroll movement (ignore if scroll position hasn't changed)
      if (Math.abs(currentScrollY - lastScrollY.current) < 3) {
        return;
      }
      
      const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
      lastScrollY.current = currentScrollY;
      
      // Show header immediately on scroll up
      if (scrollDirection === 'up') {
        isScrolling.current = false;
        setHeaderVisible(true);
        
        // Clear existing timeout
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
      } else {
        // Scroll down - hide header immediately
        isScrolling.current = true;
        setHeaderVisible(false);
        
        // Clear existing timeout before setting new one
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
        
        // Show header after half a second of no scrolling
        headerVisibleTimeoutRef.current = setTimeout(() => {
          // Only show if we're still not scrolling
          if (isScrolling.current) {
            setHeaderVisible(true);
            isScrolling.current = false;
          }
          headerVisibleTimeoutRef.current = null;
        }, 500);
      }
    };

    // Use throttling for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    if (viewMode === 'feed') {
      // For feed view, listen to window scroll
      window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    } else {
      // For grid view, listen to container scroll
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', throttledHandleScroll, { passive: true });
      }
    }
    
    return () => {
      if (viewMode === 'feed') {
        window.removeEventListener('scroll', throttledHandleScroll);
      } else {
        const container = containerRef.current;
        if (container) {
          container.removeEventListener('scroll', throttledHandleScroll);
        }
      }
      if (headerVisibleTimeoutRef.current) {
        clearTimeout(headerVisibleTimeoutRef.current);
        headerVisibleTimeoutRef.current = null;
      }
      // Reset to visible when component unmounts or view changes
      isScrolling.current = false;
      setHeaderVisible(true);
    };
  }, [viewMode]);

  // Bottom nav visibility control for feed view on mobile only
  const bottomNavScrollStopTimeoutRef = useRef(null);
  const bottomNavLastScrollY = useRef(0);
  
  useEffect(() => {
    // Always show initially
    dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
    bottomNavLastScrollY.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    
    if (!isMobile || viewMode !== 'feed') {
      // Reset to visible when not in feed view on mobile
      if (bottomNavScrollStopTimeoutRef.current) {
        clearTimeout(bottomNavScrollStopTimeoutRef.current);
        bottomNavScrollStopTimeoutRef.current = null;
      }
      return;
    }

    // For feed view on mobile, listen to window scroll for bottom nav
    const handleBottomNavScroll = () => {
      const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      
      // Always show nav at the top
      if (currentScrollY < 10) {
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
        if (bottomNavScrollStopTimeoutRef.current) {
          clearTimeout(bottomNavScrollStopTimeoutRef.current);
          bottomNavScrollStopTimeoutRef.current = null;
        }
        bottomNavLastScrollY.current = currentScrollY;
        return;
      }
      
      // Only hide/show on actual scroll movement (ignore if scroll position hasn't changed)
      if (Math.abs(currentScrollY - bottomNavLastScrollY.current) < 3) {
        return;
      }
      
      const scrollDirection = currentScrollY > bottomNavLastScrollY.current ? 'down' : 'up';
      bottomNavLastScrollY.current = currentScrollY;
      
      // Clear any existing timeout when scrolling
      if (bottomNavScrollStopTimeoutRef.current) {
        clearTimeout(bottomNavScrollStopTimeoutRef.current);
        bottomNavScrollStopTimeoutRef.current = null;
      }
      
      // Show bottom nav immediately on scroll up
      if (scrollDirection === 'up') {
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
      } else {
        // Scroll down - hide bottom nav immediately
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: false });
        
        // Set timeout to show nav after scrolling stops (500ms of no scrolling)
        bottomNavScrollStopTimeoutRef.current = setTimeout(() => {
          dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
          bottomNavScrollStopTimeoutRef.current = null;
        }, 500);
      }
    };

    // Use throttling for better performance
    let ticking = false;
    const throttledHandleBottomNavScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleBottomNavScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleBottomNavScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledHandleBottomNavScroll);
      if (bottomNavScrollStopTimeoutRef.current) {
        clearTimeout(bottomNavScrollStopTimeoutRef.current);
        bottomNavScrollStopTimeoutRef.current = null;
      }
      // Reset to visible when component unmounts or view changes
      dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
    };
  }, [isMobile, viewMode, dispatch]);

  useEffect(() => {
    if(getRatesTrigger){
      getRatesRequest(filterBy, searchState.tags);
      dispatch({ type: "GET_RATES", payload: false });
    }
  }, [getRatesTrigger]);

  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (event.data.type === 'refreshDatarate') {
        getRatesRequest(filterBy, searchState.tags);
      }
    });
    
    const handleBeforeUnload = () => {
      localStorage.removeItem('yourItemKey');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Update selected spaces in Redux when they change
  useEffect(() => {
    dispatch({
      type: "UPDATE_SEARCH",
      payload: { selectedSpaces }
    });
  }, [selectedSpaces, dispatch]);

  const deleteRate = () => {
    const rateId = removedRateId;
    let url = API_URL + `/rates/${rateId}/delete`;
    // Optimistically remove the item, keeping a snapshot to roll back on failure
    const previousData = data;
    setData((prev) => prev.filter((obj) => (obj.id ?? obj._id) !== rateId));
    axios
      .post(url, null)
      .then((res) => {
        getRatesRequest(filterBy, searchState.tags);
        setTimeout(() => {
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: "",
              type: "",
            },
          });
        }, popupTimer);
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Rate removed successfully",
            type: "success",
          },
        });
      })
      .catch((err) => {
        // Roll back the optimistic removal on failure
        setData(previousData);
        if (err?.response?.status === 401) {
          dispatch({
            type: "SET_UNAUTHORIZED",
          });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to remove rate",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };

  const onRateSuccessfully = (id) => {
    let url = API_URL + `/rates/${id}`;
    axios
      .get(url)
      .then((res) => {
        const ratedObj = res.data.rated_object.rate;
        setData((prev) => prev.map((obj) => (obj.id === id ? ratedObj : obj)));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({
            type: "SET_UNAUTHORIZED",
          });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to refresh rate",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };
  const token = useSelector((state) => state.token);
  const filterRates = useSelector((state) => state.filterRates);

  // Clear any stale AI filter from other pages on mount/unmount
  useEffect(() => {
    dispatch({ type: 'FILTER_RATES', payload: null });
    return () => {
      dispatch({ type: 'FILTER_RATES', payload: null });
    };
  }, []);

  // When filterRates is set from /chats/ai (filter_data), fetch those rates via API (like SpaceRates)
  useEffect(() => {
    if (!Array.isArray(filterRates)) return; // null/undefined = initial, do nothing
    if (filterRates.length > 0) {
      if (typeof filterRates[0] === 'string') {
        setRatesPage(0);
        setHasMoreRates(false);
        getRatesRequest(filterBy, searchState.tags, 0, false, filterRates);
      } else {
        setData(filterRates);
      }
    } else {
      setRatesPage(0);
      setHasMoreRates(true);
      getRatesRequest(filterBy, searchState.tags, 0, false, null);
    }
  }, [filterRates]);

  useEffect(() => {
    let isExtension = localStorage.getItem('is_extension_login')
    if (isExtension) {
      // Clear the flag immediately to prevent redirect loop
      localStorage.removeItem('is_extension_login')
      navigate('/extension-login-success')
    }
  }, []);

  const getRatesRequest = useCallback((search_type, tags, page = 0, append = false, rateIds = null) => {
    setIsLoading(!append);
    let url = API_URL + `/rates/my-rates`;
    let paramsBody = {
      tags: tags?.length > 0 ? tags.join(',') : null,
      spaces: selectedSpaces.length > 0 ? selectedSpaces.join(',') : null,
      start_from: page * 10,
      size: 10
    };
    if (organizationId) {
      paramsBody.organization_id = organizationId;
    }
    if (rateIds && Array.isArray(rateIds) && rateIds.length > 0) {
      paramsBody.rating_ids = rateIds.join(',');
    }
    // Default search is semantic (vector); force exact keyword search only
    // when the user ticks the "Exact search" box.
    if (exactSearch) {
      paramsBody.exact_search = true;
    }
    axios
      .get(url, {
        params: paramsBody,
      })
      .then((res) => {
        const rates = res.data.rates || [];
        if (append) {
          setData(prev => [...prev, ...rates]);
        } else {
          setData(rates);
        }
        // If we got less than 10 items, there are no more
        setHasMoreRates(rates.length === 10);
        setIsLoadingMore(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoadingMore(false);
        setIsLoading(false);
        setHasMoreRates(false);
        if (err.response && err.response.status === 401) {
          dispatch({
            type: "SET_UNAUTHORIZED",
          });
          return;
        }
      });
  }, [selectedSpaces, dispatch, organizationId, exactSearch]);

  // Handle scroll event - works for both feed view (window scroll) and grid view (container scroll)
  const handleScroll = useCallback(() => {
    if (isLoadingMore || !hasMoreRates) return;

    // For feed view, use window scroll; for grid view, use container scroll
    let scrollTop, scrollHeight, clientHeight;
    
    if (viewMode === 'feed') {
      scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight || document.documentElement.clientHeight;
    } else {
      if (!containerRef.current) return;
      const container = containerRef.current;
      scrollTop = container.scrollTop;
      scrollHeight = container.scrollHeight;
      clientHeight = container.clientHeight;
    }

    const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.5;

    if (isNearBottom && hasMoreRates && !isLoadingMore) {
      setIsLoadingMore(true);
      const nextPage = ratesPage + 1;
      setRatesPage(nextPage);
      getRatesRequest(filterBy, searchState.tags, nextPage, true);
    }
  }, [ratesPage, isLoadingMore, hasMoreRates, filterBy, searchState.tags, getRatesRequest, viewMode]);

  // Add scroll event listener - window for feed view, container for grid view
  useEffect(() => {
    if (viewMode === 'feed') {
      // For feed view, listen to window scroll
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      // For grid view, listen to container scroll
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }
  }, [handleScroll, viewMode]);

  // Search is driven by the header search bar (Redux `search.tags`); the effect
  // below refetches whenever those tags or the "Exact search" flag change.

  useEffect(() => {
    if (token) {
      getRatesRequest(filterBy, searchState.tags);
    }

  }, [filterBy, searchState.tags, token, selectedSpaces, getRatesRequest]);

  useEffect(() => {
    const count = selectedSpaces.length;
    setActiveFiltersCount(count);
  }, [selectedSpaces]);

  // Open mobile modal when filter is shown on mobile
  useEffect(() => {
    if (isMobile && showSpaceFilter && mobileSpacesData.length > 0) {
      const timer = setTimeout(() => {
        setShowMobileSpaceModal(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowMobileSpaceModal(false);
    }
  }, [isMobile, showSpaceFilter, mobileSpacesData]);

  const handleSpacesReady = (spaces) => {
    setMobileSpacesData(spaces);
  };

  const handleMobileSpaceApply = (selectedIds) => {
    setSelectedSpaces(selectedIds);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setRatesPage(0);
    setHasMoreRates(true);
    setIsLoadingMore(false);
  }, [filterBy, searchState.tags, selectedSpaces, exactSearch]);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpenFilterMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuCheckbox = (name) => {
    setFilterBy(name);
    setAnchorEl(null);
  };

  // Handle "Add to Space" functionality
  const showSpacePopup = (rateId) => {
    if (authorized) {
      setSelectedRateId(rateId);
      setOpenSpaceModal(true);
    } else {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "You must Login to Perform this Action",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "",
            type: "",
          },
        });
      }, popupTimer);
    }
  };

  const handleSpaceModalClose = () => {
    setOpenSpaceModal(false);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  return (
    <>
      <Box sx={{ 
        minHeight: '100vh',
        background: viewMode === 'feed' ? '#fafafa' : 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)',
        pt: viewMode === 'feed' ? { xs: 0, sm: 0 } : { xs: 1, sm: 4, md: 6 },
        px: viewMode === 'feed' ? 0 : { xs: viewMode === 'feed' ? 0.5 : 1, sm: 3, md: 4 },
        pb: viewMode === 'feed' ? { xs: 8, sm: 4 } : 0
      }}>
      <Box sx={{ maxWidth: viewMode === 'feed' ? '100%' : '1400px', mx: 'auto' }}>
        <Box sx={{ 
          mb: viewMode === 'feed' ? 0 : { xs: 1, sm: 2 },
          position: viewMode === 'feed' ? 'sticky' : 'relative',
          top: 0,
          zIndex: 10,
          background: viewMode === 'feed' ? '#fff' : 'transparent',
          borderBottom: viewMode === 'feed' ? '1px solid #efefef' : 'none',
          pb: viewMode === 'feed' ? { xs: 1, sm: 1.5 } : 0,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: viewMode === 'feed' ? 0 : { xs: 0.5, sm: 1 },
            px: viewMode === 'feed' ? { xs: 1, sm: 2 } : { xs: 0.5, sm: 1 },
            py: viewMode === 'feed' ? { xs: 1, sm: 1.5 } : { xs: 0.25, sm: 0.5 },
            maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '100%',
            mx: viewMode === 'feed' ? 'auto' : 0
          }}>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                fontWeight: viewMode === 'feed' ? 600 : 700,
                fontSize: viewMode === 'feed' ? { xs: '1.1rem', sm: '1.25rem' } : { xs: '1rem', sm: '1.25rem', md: '1.4rem' },
                background: viewMode === 'feed' ? 'none' : 'linear-gradient(135deg, #1a202c 0%, #6366f1 100%)',
                color: viewMode === 'feed' ? '#262626' : 'transparent',
                WebkitBackgroundClip: viewMode === 'feed' ? 'unset' : 'text',
                WebkitTextFillColor: viewMode === 'feed' ? '#262626' : 'transparent',
                backgroundClip: viewMode === 'feed' ? 'unset' : 'text',
                mb: 0
              }}
            >
              {pageTitle}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {/* View Mode Toggle - Hidden on mobile */}
              {!isMobile && (
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  sx={{
                    background: viewMode === 'feed' ? 'transparent' : 'rgba(255,255,255,0.8)',
                    borderRadius: '8px',
                    border: viewMode === 'feed' ? 'none' : '1px solid rgba(226,232,240,0.6)',
                    boxShadow: viewMode === 'feed' ? 'none' : '0 2px 8px rgba(0,0,0,0.04)',
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.75,
                      color: viewMode === 'feed' ? '#8e8e8e' : 'rgba(71,85,105,0.8)',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: viewMode === 'feed' ? 'rgba(0,0,0,0.05)' : 'rgba(99,102,241,0.1)',
                        color: viewMode === 'feed' ? '#262626' : 'rgba(99,102,241,0.8)',
                      },
                      '&.Mui-selected': {
                        background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: viewMode === 'feed' ? '#262626' : 'white',
                        boxShadow: viewMode === 'feed' ? 'none' : '0 4px 12px rgba(99,102,241,0.3)',
                        '&:hover': {
                          background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                          boxShadow: viewMode === 'feed' ? 'none' : '0 6px 16px rgba(99,102,241,0.4)',
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="grid" aria-label="grid view">
                    <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="feed" aria-label="feed view">
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              
              <Tooltip title={activeFiltersCount > 0 ? "Show active filters" : "Filter"} arrow>
                <IconButton 
                  onClick={() => setShowSpaceFilter(!showSpaceFilter)}
                  size="small"
                  sx={{ 
                    background: viewMode === 'feed' ? 'transparent' : (showSpaceFilter 
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                      : 'rgba(255,255,255,0.8)'),
                    backdropFilter: viewMode === 'feed' ? 'none' : 'blur(10px)',
                    border: viewMode === 'feed' ? 'none' : '1px solid rgba(226,232,240,0.6)',
                    borderRadius: viewMode === 'feed' ? '50%' : '12px',
                    width: viewMode === 'feed' ? 36 : 48,
                    height: viewMode === 'feed' ? 36 : 48,
                    color: viewMode === 'feed' ? '#262626' : (showSpaceFilter ? 'white' : 'rgba(71,85,105,0.8)'),
                    boxShadow: viewMode === 'feed' ? 'none' : (showSpaceFilter 
                      ? '0 4px 12px rgba(99,102,241,0.3)' 
                      : '0 2px 8px rgba(0,0,0,0.04)'),
                    '&:hover': {
                      background: viewMode === 'feed' 
                        ? 'rgba(0,0,0,0.05)' 
                        : (showSpaceFilter 
                          ? 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)'
                          : 'rgba(99,102,241,0.1)'),
                      borderColor: viewMode === 'feed' ? 'none' : 'rgba(99,102,241,0.3)',
                      transform: viewMode === 'feed' ? 'none' : 'translateY(-2px)',
                      boxShadow: viewMode === 'feed' ? 'none' : (showSpaceFilter 
                        ? '0 6px 16px rgba(99,102,241,0.4)'
                        : '0 4px 12px rgba(99,102,241,0.15)'),
                      color: viewMode === 'feed' ? '#262626' : (showSpaceFilter ? 'white' : 'rgba(99,102,241,0.8)')
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Badge 
                    badgeContent={activeFiltersCount} 
                    sx={{
                      '& .MuiBadge-badge': {
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        boxShadow: '0 2px 4px rgba(239,68,68,0.3)',
                        fontSize: '11px',
                        fontWeight: 600
                      }
                    }}
                    invisible={activeFiltersCount === 0}
                  >
                    <FilterListIcon sx={{ fontSize: viewMode === 'feed' ? 18 : 20 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {showSpaceFilter && (
            <Box sx={{ 
              maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '1200px',
              background: viewMode === 'feed' ? '#fff' : 'rgba(255,255,255,0.7)',
              backdropFilter: viewMode === 'feed' ? 'none' : 'blur(20px)',
              WebkitBackdropFilter: viewMode === 'feed' ? 'none' : 'blur(20px)',
              borderRadius: 0,
              p: { xs: 1.5, sm: viewMode === 'feed' ? 2 : 3 },
              border: viewMode === 'feed' ? '1px solid #efefef' : '1px solid rgba(226,232,240,0.6)',
              borderTop: '1px solid #efefef',
              borderLeft: 'none',
              borderRight: 'none',
              boxShadow: viewMode === 'feed' ? 'none' : '0 4px 16px rgba(0,0,0,0.06)',
              mb: 0,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: { xs: 'none', sm: 'block' },
              mx: viewMode === 'feed' ? 'auto' : 0
            }}>
              <SpacesFilter 
                onSelect={(s) => { setSelectedSpaces(s) }} 
                searchValue={searchState.query}
                onSpacesReady={handleSpacesReady}
              />
            </Box>
          )}
          {showSpaceFilter && isMobile && (
            <SpacesFilter 
              onSelect={(s) => { setSelectedSpaces(s) }} 
              searchValue={searchState.query}
              onSpacesReady={handleSpacesReady}
            />
          )}
        </Box>

        {/* AI Filter Active Banner */}
        {Array.isArray(filterRates) && filterRates.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: viewMode === 'feed' ? { xs: 1.5, sm: 2 } : { xs: 1, sm: 2 },
              py: 1,
              maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '100%',
              mx: viewMode === 'feed' ? 'auto' : 0,
              background: 'rgba(99, 102, 241, 0.07)',
              borderBottom: '1px solid rgba(99, 102, 241, 0.18)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 500 }}>
              AI filter active &mdash; {filterRates.length} result{filterRates.length !== 1 ? 's' : ''}
            </Typography>
            <Button
              size="small"
              onClick={() => dispatch({ type: 'FILTER_RATES', payload: [] })}
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{ color: '#6366f1', textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', minWidth: 0 }}
            >
              Clear filter
            </Button>
          </Box>
        )}

        {!token && (
          <Box 
            sx={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.8) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              p: 6, 
              borderRadius: '24px', 
              border: '1px solid rgba(226,232,240,0.6)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              mt: 6,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                borderRadius: '24px 24px 0 0'
              }
            }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'rgba(30,41,59,0.9)', 
                mb: 3,
                fontWeight: 600,
                fontSize: '1.5rem'
              }}
            >
              🔐 Authentication Required
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(71,85,105,0.8)', 
                mb: 4,
                fontSize: '1.1rem',
                lineHeight: 1.6
              }}
            >
              To access your personalized AI-powered reviews and insights, please{' '}
              <Box 
                component="span" 
                sx={{ 
                  color: '#6366f1', 
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
                onClick={() => navigate('/login')}
              >
                sign in
              </Box>
              {' '}to your account.
            </Typography>
            <Button
              onClick={() => navigate('/login')}
              variant="contained"
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                px: 5,
                py: 2,
                borderRadius: '14px',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'white',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              🚀 Sign In Now
            </Button>
          </Box>
        )}
        
        <Box
          ref={containerRef}
          sx={{ 
            mt: viewMode === 'feed' ? 0 : { xs: 1, sm: 3, md: 4 },
            background: viewMode === 'feed' ? '#fafafa' : 'transparent',
            minHeight: viewMode === 'feed' ? '100vh' : 'auto',
            maxHeight: viewMode === 'feed' ? 'none' : '80vh',
            overflowY: viewMode === 'feed' ? 'auto' : 'auto',
            p: viewMode === 'feed' ? 0 : { xs: 0.5, sm: 2 },
            maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '1400px',
            mx: 'auto',
            width: '100%',
            // Custom scrollbar - minimal for feed view
            '&::-webkit-scrollbar': {
              width: viewMode === 'feed' ? '0px' : '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '10px',
              '&:hover': {
                background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
              }
            },
          }}
        >
          <Grid
            container
            spacing={viewMode === 'feed' ? 0 : 3}
            sx={{ 
              width: '100%',
              margin: 0,
              '& .MuiGrid-item': {
                padding: viewMode === 'feed' ? { xs: '4px 0', sm: '8px 0' } : undefined
              }
            }}
          >
            {isLoading ? (
              // Loading skeletons
              [...Array(6)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`} sx={{ 
                  display: 'flex',
                  minHeight: '350px'
                }}>
                  <Paper 
                    sx={{ 
                      height: viewMode === 'feed' ? 'auto' : '330px', 
                      marginBottom: '10px', 
                      padding: '20px',
                      borderRadius: '16px',
                      background: 'rgba(255,255,255,0.8)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(226,232,240,0.6)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      }
                    }}
                  >
                    <Skeleton 
                      variant="rectangular" 
                      height={200} 
                      sx={{ 
                        borderRadius: '12px', 
                        mb: 2,
                        background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                      }} 
                    />
                    <Skeleton 
                      variant="text" 
                      height={28} 
                      width="80%" 
                      sx={{ 
                        mb: 1,
                        borderRadius: '8px',
                        background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                      }} 
                    />
                    <Skeleton 
                      variant="text" 
                      height={20} 
                      width="60%" 
                      sx={{ 
                        mb: 2,
                        borderRadius: '6px',
                        background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                      }} 
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      <Skeleton 
                        variant="rectangular" 
                        height={36} 
                        width={100} 
                        sx={{ 
                          borderRadius: '10px',
                          background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                        }} 
                      />
                      <Skeleton 
                        variant="rectangular" 
                        height={36} 
                        width={100} 
                        sx={{ 
                          borderRadius: '10px',
                          background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                        }} 
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))
        ) : (
          <>
            {data.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: { xs: 8, sm: 12 },
                  px: { xs: 2, sm: 4 },
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                  }}>
                    <StarOutlineIcon sx={{ 
                      fontSize: { xs: 40, sm: 50 },
                      color: 'rgba(99,102,241,0.6)'
                    }} />
                  </Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 600,
                    color: 'rgba(30,41,59,0.9)',
                    mb: 1.5,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    {organizationId ? 'No Organization Reviews Yet' : 'No Reviews Yet'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: 'rgba(71,85,105,0.7)',
                    maxWidth: '500px',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.6
                  }}>
                    {organizationId 
                      ? 'You haven\'t created any reviews in your organization yet. Start by creating your first review!'
                      : 'There are no reviews to display yet. Start exploring and rate your favorite items!'}
                  </Typography>
                </Box>
              </Grid>
            ) : (
              <>
                {data.map((m, index) => (
                  <Grid 
                    item 
                    xs={viewMode === 'feed' ? 12 : 12} 
                    sm={viewMode === 'feed' ? 12 : 6} 
                    md={viewMode === 'feed' ? 12 : 4} 
                    key={index}
                    sx={{ 
                      display: 'flex',
                      minHeight: viewMode === 'grid' ? '350px' : 'auto'
                    }}
                  >
                <Box sx={{
                  height: viewMode === 'feed' ? '100%' : '100%',
                  maxWidth: viewMode === 'feed' ? '100%' : 'auto',
                  margin: viewMode === 'feed' ? '0 auto' : 'auto',
                  minHeight: viewMode === 'grid' ? '330px' : 'auto',
                  display: 'flex',
                  width: '100%',
                  marginBottom: viewMode === 'feed' ? { xs: 1, sm: 3 } : '10px',
                  padding: viewMode === 'feed' ? 0 : '10px',
                }}>
                  {viewMode === 'grid' ? (
                    <HomeCardItem
                      selected={selectedCards.includes(m.id)}
                      isBookmark={true}
                      data={m}
                      searhTags={searchState.tags}
                      applyMargin={true}
                      displayAddToSpace={true}
                      showRemove={true}
                      enableDragAndDrop={true}
                      hideUserInfo={true}
                      onRemove={(id) => {
                        setRemoveDialogOpen(true);
                        setRemovedRateId(id);
                      }}
                      onDelete={(id) => {
                        setRemoveDialogOpen(true);
                        setRemovedRateId(id);
                      }}
                      onRateSuccessfully={onRateSuccessfully}
                      showSpacePopup={showSpacePopup}
                    />
                  ) : (
                    <FeedCardItem
                      selected={selectedCards.includes(m.id)}
                      isBookmark={true}
                      data={m}
                      searhTags={searchState.tags}
                      applyMargin={true}
                      displayAddToSpace={true}
                      showRemove={true}
                      enableDragAndDrop={true}
                      onRemove={(id) => {
                        setRemoveDialogOpen(true);
                        setRemovedRateId(id);
                      }}
                      onDelete={(id) => {
                        setRemoveDialogOpen(true);
                        setRemovedRateId(id);
                      }}
                      onRateSuccessfully={onRateSuccessfully}
                      showSpacePopup={showSpacePopup}
                    />
                  )}
                </Box>
              </Grid>
            ))}
                {isLoadingMore && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" p={4}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    p: 3,
                    border: '1px solid rgba(226,232,240,0.6)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                  }}>
                    <CircularProgress 
                      size={24}
                      sx={{
                        color: '#6366f1',
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round',
                        }
                      }}
                    />
                    <Typography sx={{ 
                      color: 'rgba(71,85,105,0.8)',
                      fontWeight: 500
                    }}>
                      Loading more insights...
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
              </>
            )}
          </>
        )}
          </Grid>
        </Box>
      </Box>
      </Box>
      
      <ConfirmationDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={() => {
          deleteRate();
          setRemoveDialogOpen(false);
        }}
        title={"Delete rate"}
        cancelButtonString={"Cancel"}
        confirmButtonString={"Delete"}
        message={"Are you sure you want to delete this rate?"}
      />

      {/* Add to Space Dialog */}
      {openSpaceModal && (
        <AddToSpace
          open={openSpaceModal}
          rateId={selectedRateId}
          objectId={selectedRateId}
          itemData={data.find(item => (item.id ?? item._id) === selectedRateId)}
          currentSpaceId={null} // MyReviews doesn't have current space context
          handleClose={handleSpaceModalClose}
          authorized={authorized}
        />
      )}
      
      <AiDialog open={openAiDialog} onClose={() => { setOpenAiDialog(false) }} />
      
      {/* Mobile Filter Modal - Outside filter component */}
      {isMobile && (
        <MobileFilterView
          isOpen={showMobileSpaceModal}
          onClose={() => {
            setShowMobileSpaceModal(false);
            setShowSpaceFilter(false);
          }}
          items={mobileSpacesData}
          selectedItems={selectedSpaces}
          onApply={handleMobileSpaceApply}
          title="Filter by Spaces"
          type="spaces"
        />
      )}
    </>
  );
};

export default Reviews;
